import { useCallback, useEffect, useRef, useState } from "react";
import type { EvidenceUploadMeta } from "@rma-orbit/shared";

import { api } from "../api/client.js";
import { agentGet, agentPost, type AgentState } from "../lib/agent.js";
import { ErrorNotice, Notice } from "../lib/ui.js";

/**
 * SSH / Serial 콘솔 (harness/UI-SPEC.md §4, harness/EVIDENCE-POLICY.md, C71).
 *
 * 레퍼런스(triznrnd.kro.kr 콘솔)처럼: [+ SSH 연결] [+ Serial 연결] 로 세션을 열고, 세션마다 탭이
 * 생기며, 아래 터미널에서 **직접 타이핑**한다.
 *   · SSH — 이 PC 의 수집기(127.0.0.1:8788)가 대신 붙어 한 줄씩 돌린다. 사이트(Workers)는 사무실
 *     망에 닿지 않는다. 비밀번호는 세션이 살아 있는 동안 브라우저 메모리에만 있다
 *   · Serial — 브라우저가 `navigator.serial` 로 직접 붙는다. 보레이트·데이터 비트·패리티·정지 비트·
 *     흐름 제어·로컬 에코를 고를 수 있다
 *
 * 화면에 찍힌 것은 **원문 그대로** 증적으로 올린다. 판정은 하지 않는다 — `I/O error` 같은 줄은
 * 표시만 한다(SOUL S5).
 */

const HIGHLIGHT = /(i\/o error|input\/output error|buffer i\/o|blk_update_request|ata\d+.*(error|failed)|emergency mode|read-only file system|segfault)/i;

const QUICK: Array<{ label: string; command: string }> = [
  { label: "dmesg", command: "dmesg | tail -n 100" },
  { label: "messages", command: "tail -n 100 /var/log/messages 2>/dev/null || journalctl -n 100 --no-pager" },
  { label: "디스크", command: "df -h; cat /proc/mdstat 2>/dev/null" },
  { label: "버전", command: "cat /etc/version 2>/dev/null; uname -a" },
  { label: "VSI0", command: "lxc-ls -f 2>/dev/null || lxc-ls" },
];

type SerialPortLike = {
  open(opts: { baudRate: number; dataBits?: number; parity?: string; stopBits?: number; flowControl?: string }): Promise<void>;
  close(): Promise<void>;
  readable: ReadableStream<Uint8Array> | null;
  writable: WritableStream<Uint8Array> | null;
  setSignals?(s: { break?: boolean }): Promise<void>;
};

interface Line {
  text: string;
  kind: "in" | "out" | "err" | "sys";
}

interface Session {
  id: string;
  type: "ssh" | "serial";
  label: string;
  lines: Line[];
  connected: boolean;
  // ssh
  host?: string;
  port?: number;
  username?: string;
  password?: string;
  // serial
  port_obj?: SerialPortLike;
  reader?: ReadableStreamDefaultReader<Uint8Array>;
  writer?: WritableStreamDefaultWriter<Uint8Array>;
  localEcho?: boolean;
  history: string[];
  /** 접속하면서 같이 정한 학습 계획 — 있으면 터미널이 학습 란을 바로 연다 */
  learn?: LearnPlan;
}

interface LearnPlan {
  vendor: string;
  model: string;
  execute: boolean;
  autoStart: boolean;
}

let seq = 0;

export function Console({ can }: { can: (p: string) => boolean }) {
  const [state, setState] = useState<AgentState | null>(null);
  const [agentDown, setAgentDown] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<"none" | "ssh" | "serial">("none");
  const [sessions, setSessions] = useState<Session[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setState(await agentGet());
      setAgentDown(false);
    } catch {
      setAgentDown(true);
      setState(null);
    }
  }, []);

  useEffect(() => {
    void load();
    const t = window.setInterval(() => void load(), 3000);
    return () => window.clearInterval(t);
  }, [load]);

  const active = sessions.find((s) => s.id === activeId) ?? null;
  const serialSupported = typeof navigator !== "undefined" && "serial" in navigator;

  function patch(id: string, fn: (s: Session) => Session) {
    setSessions((prev) => prev.map((s) => (s.id === id ? fn(s) : s)));
  }
  function append(id: string, line: Line) {
    patch(id, (s) => ({ ...s, lines: [...s.lines, line].slice(-4000) }));
  }

  function openSsh(host: string, port: number, username: string, password: string, learn?: LearnPlan) {
    const id = `s${++seq}`;
    const session: Session = {
      id,
      type: "ssh",
      label: `${username}@${host}`,
      learn,
      lines: [{ kind: "sys", text: `# ${username}@${host}:${port} — 수집기가 대신 붙습니다. 한 줄에 명령 하나.` }],
      connected: true,
      host,
      port,
      username,
      password,
      history: [],
    };
    setSessions((prev) => [...prev, session]);
    setActiveId(id);
    setForm("none");
    localStorage.setItem("orbit.ssh.host", host);
    localStorage.setItem("orbit.ssh.port", String(port));
    localStorage.setItem("orbit.ssh.user", username);
  }

  async function openSerial(opts: { baudRate: number; dataBits: number; parity: string; stopBits: number; flowControl: string; localEcho: boolean }, learn?: LearnPlan) {
    setError(null);
    try {
      const serial = (navigator as unknown as { serial: { requestPort(): Promise<SerialPortLike> } }).serial;
      const port = await serial.requestPort();
      await port.open({ baudRate: opts.baudRate, dataBits: opts.dataBits, parity: opts.parity, stopBits: opts.stopBits, flowControl: opts.flowControl });
      const id = `s${++seq}`;
      const writer = port.writable?.getWriter();
      const session: Session = {
        id,
        type: "serial",
        label: `Serial ${opts.baudRate}`,
        learn,
        lines: [{ kind: "sys", text: `# 시리얼 ${opts.baudRate}-${opts.dataBits}-${opts.parity[0]!.toUpperCase()}-${opts.stopBits} 연결됨` }],
        connected: true,
        port_obj: port,
        writer,
        localEcho: opts.localEcho,
        history: [],
      };
      setSessions((prev) => [...prev, session]);
      setActiveId(id);
      setForm("none");

      // 읽기 루프 — 줄 단위로 화면에 붙인다. 원문을 다듬지 않는다
      void (async () => {
        const decoder = new TextDecoder();
        const reader = port.readable!.getReader();
        patch(id, (s) => ({ ...s, reader }));
        let buf = "";
        try {
          for (;;) {
            const { value, done } = await reader.read();
            if (done) break;
            buf += decoder.decode(value, { stream: true });
            const parts = buf.split(/\r?\n/);
            buf = parts.pop() ?? "";
            for (const p of parts) append(id, { kind: "out", text: p });
          }
        } catch {
          /* 끊김 */
        }
        if (buf) append(id, { kind: "out", text: buf });
        append(id, { kind: "sys", text: "# 연결이 끊겼습니다" });
        patch(id, (s) => ({ ...s, connected: false }));
      })();
    } catch (err) {
      setError(err);
    }
  }

  async function send(session: Session, command: string) {
    if (session.type === "ssh") {
      append(session.id, { kind: "in", text: command });
      patch(session.id, (s) => ({ ...s, history: [...s.history, command] }));
      try {
        const res = await agentPost<{ ok: boolean; error: string | null; results: Array<{ stdout: string; stderr: string; exit_status: number | null }> }>(
          "/ssh/exec",
          { host: session.host, port: session.port, username: session.username, password: session.password, command },
        );
        const r = res.results?.[0];
        if (r) {
          for (const l of r.stdout.replace(/\n$/, "").split("\n")) if (r.stdout) append(session.id, { kind: "out", text: l });
          for (const l of r.stderr.replace(/\n$/, "").split("\n")) if (r.stderr) append(session.id, { kind: "err", text: l });
          if (r.exit_status !== 0 && r.exit_status !== null) append(session.id, { kind: "sys", text: `[exit ${r.exit_status}]` });
        }
        if (!res.ok && res.error) append(session.id, { kind: "err", text: res.error });
      } catch (err) {
        append(session.id, { kind: "err", text: err instanceof Error ? err.message : String(err) });
      }
    } else {
      if (session.localEcho) append(session.id, { kind: "in", text: command });
      patch(session.id, (s) => ({ ...s, history: [...s.history, command] }));
      try {
        await session.writer?.write(new TextEncoder().encode(command + "\r\n"));
      } catch (err) {
        append(session.id, { kind: "err", text: err instanceof Error ? err.message : String(err) });
      }
    }
  }

  async function close(session: Session) {
    if (session.type === "serial") {
      try {
        await session.reader?.cancel();
        session.reader?.releaseLock();
        session.writer?.releaseLock();
        await session.port_obj?.close();
      } catch {
        /* 이미 닫혔다 */
      }
    }
    setSessions((prev) => prev.filter((s) => s.id !== session.id));
    if (activeId === session.id) setActiveId(sessions.find((s) => s.id !== session.id)?.id ?? null);
  }

  async function sendBreak(session: Session) {
    try {
      await session.port_obj?.setSignals?.({ break: true });
      await new Promise((r) => setTimeout(r, 250));
      await session.port_obj?.setSignals?.({ break: false });
      append(session.id, { kind: "sys", text: "# break" });
    } catch (err) {
      append(session.id, { kind: "err", text: err instanceof Error ? err.message : String(err) });
    }
  }

  return (
    <>
      <div className="admin-head">
        <span className="navicon" aria-hidden="true">{">_"}</span>
        <div>
          <h1>SSH / Serial 콘솔</h1>
          <p className="page-desc" style={{ margin: 0 }}>
            직접 타이핑합니다. 찍힌 것은 원문 그대로 증적으로 올릴 수 있고, 판정은 하지 않습니다 — 의심되는 줄만 표시합니다.
          </p>
        </div>
      </div>
      <ErrorNotice error={error} />

      <div className="row" style={{ marginBottom: 12 }}>
        <button className="primary" onClick={() => setForm(form === "ssh" ? "none" : "ssh")}>+ SSH 연결</button>
        <button className="serial-btn" onClick={() => setForm(form === "serial" ? "none" : "serial")} disabled={!serialSupported} title={serialSupported ? "" : "Chrome/Edge 89+ 가 필요합니다"}>
          + Serial 연결
        </button>
        <span className="muted" style={{ fontSize: 12, marginLeft: 6 }}>
          <span className={`dot ${!agentDown && state?.ssh_ready !== false ? "available" : ""}`} style={{ marginRight: 6 }} />
          수집기 {agentDown ? "꺼짐 — start-collector.bat 을 켜 두세요" : state?.ssh_ready === false ? "SSH 부품 없음 (bat 창을 다시 여세요)" : "준비됨"}
        </span>
      </div>

      {sessions.length > 0 && (
        <div className="con-tabs">
          {sessions.map((s) => (
            <button key={s.id} className={`con-tab ${s.id === activeId ? "active" : ""} ${s.type}`} onClick={() => setActiveId(s.id)}>
              <span className={`dot ${s.connected ? "available" : ""}`} />
              {s.label}
              <span
                className="con-tab-x"
                role="button"
                aria-label="닫기"
                onClick={(e) => { e.stopPropagation(); void close(s); }}
              >
                ×
              </span>
            </button>
          ))}
        </div>
      )}

      {form === "ssh" && <SshForm agentDown={agentDown} onConnect={openSsh} />}
      {form === "serial" && <SerialForm onConnect={(o, learn) => void openSerial(o, learn)} />}

      {active ? (
        <Terminal
          session={active}
          canUpload={can("evidence.write")}
          onSend={(cmd) => void send(active, cmd)}
          onClear={() => patch(active.id, (s) => ({ ...s, lines: [] }))}
          onBreak={() => void sendBreak(active)}
          onNote={(text) => append(active.id, { kind: "sys", text })}
          onError={setError}
        />
      ) : (
        <div className="card con-empty">
          <div className="term-preview">
            <div className="term-head"><span className="dots"><i /><i /><i /></span>rma-orbit console</div>
            <pre className="mono terminal" style={{ minHeight: 160 }}>
              {"$ 위의 [+ SSH 연결] 또는 [+ Serial 연결] 로 세션을 여세요.\n$ 세션은 탭으로 여러 개 열 수 있습니다.\n$ 화면에 찍힌 것은 [증적으로 저장] 으로 케이스에 붙습니다."}
            </pre>
          </div>
        </div>
      )}
    </>
  );
}

/* ---------------- 연결 폼 ---------------- */

function SshForm({ agentDown, onConnect }: { agentDown: boolean; onConnect: (host: string, port: number, user: string, pass: string, learn?: LearnPlan) => void }) {
  const [host, setHost] = useState(() => localStorage.getItem("orbit.ssh.host") ?? "");
  const [port, setPort] = useState(() => localStorage.getItem("orbit.ssh.port") ?? "22");
  const [user, setUser] = useState(() => localStorage.getItem("orbit.ssh.user") ?? "admin");
  const [pass, setPass] = useState("");
  const [plan, setPlan] = useState<LearnPlan>({ vendor: "", model: "", execute: false, autoStart: true });
  const ok = host.trim() && user.trim() && pass;
  const go = () => onConnect(host.trim(), Number(port) || 22, user.trim(), pass, plan.autoStart || plan.vendor || plan.model ? plan : undefined);
  return (
    <div className="card con-form">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <label className="field">호스트<input value={host} onChange={(e) => setHost(e.target.value)} placeholder="192.168.1.1" style={{ width: 150 }} /></label>
        <label className="field">포트<input value={port} onChange={(e) => setPort(e.target.value)} style={{ width: 64 }} /></label>
        <label className="field">계정<input value={user} onChange={(e) => setUser(e.target.value)} placeholder="admin" style={{ width: 100 }} /></label>
        <label className="field">비밀번호<input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="••••••" style={{ width: 120 }} onKeyDown={(e) => { if (e.key === "Enter" && ok && !agentDown) go(); }} /></label>
        <button className="primary" disabled={!ok || agentDown} onClick={go}>접속</button>
        {agentDown && <span className="muted">수집기가 꺼져 있어 SSH 를 열 수 없습니다.</span>}
      </div>
      <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>비밀번호는 이 탭이 열려 있는 동안만 브라우저에 있고, 수집기까지만 갑니다. 어디에도 저장되지 않습니다.</p>
      <DevicePanel
        plan={plan}
        onPlan={setPlan}
        via="ssh"
        onPick={(d) => {
          if (d.host) setHost(d.host);
          if (d.port) setPort(String(d.port));
          if (d.username) setUser(d.username);
          setPlan((p) => ({ ...p, vendor: d.vendor, model: d.model }));
        }}
      />
    </div>
  );
}

function SerialForm({ onConnect }: { onConnect: (o: { baudRate: number; dataBits: number; parity: string; stopBits: number; flowControl: string; localEcho: boolean }, learn?: LearnPlan) => void }) {
  const [baud, setBaud] = useState("115200");
  const [bits, setBits] = useState("8");
  const [parity, setParity] = useState("none");
  const [stop, setStop] = useState("1");
  const [flow, setFlow] = useState(false);
  const [echo, setEcho] = useState(true);
  const [plan, setPlan] = useState<LearnPlan>({ vendor: "", model: "", execute: false, autoStart: true });
  return (
    <div className="card con-form">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <button className="serial-btn" onClick={() => onConnect({ baudRate: Number(baud), dataBits: Number(bits), parity, stopBits: Number(stop), flowControl: flow ? "hardware" : "none", localEcho: echo }, plan.autoStart || plan.vendor || plan.model ? plan : undefined)}>
          Connect — 포트 고르기
        </button>
        <label className="field">Baud rate
          <select value={baud} onChange={(e) => setBaud(e.target.value)}>
            {["9600", "19200", "38400", "57600", "115200", "230400", "460800", "921600"].map((b) => <option key={b}>{b}</option>)}
          </select>
        </label>
        <label className="field">Data bits
          <select value={bits} onChange={(e) => setBits(e.target.value)}>{["7", "8"].map((b) => <option key={b}>{b}</option>)}</select>
        </label>
        <label className="field">Parity
          <select value={parity} onChange={(e) => setParity(e.target.value)}>
            <option value="none">None</option><option value="even">Even</option><option value="odd">Odd</option>
          </select>
        </label>
        <label className="field">Stop bits
          <select value={stop} onChange={(e) => setStop(e.target.value)}>{["1", "2"].map((b) => <option key={b}>{b}</option>)}</select>
        </label>
        <label className="row tight" style={{ fontSize: 12, color: "var(--ink-3)" }}><input type="checkbox" checked={flow} onChange={(e) => setFlow(e.target.checked)} /> Hardware flow control</label>
        <label className="row tight" style={{ fontSize: 12, color: "var(--ink-3)" }}><input type="checkbox" checked={echo} onChange={(e) => setEcho(e.target.checked)} /> Local echo</label>
      </div>
      <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>USB-Serial 콘솔 115200-8-N-1 이 기본입니다. 브라우저가 직접 붙고 서버는 포트를 만지지 않습니다.</p>
      <DevicePanel plan={plan} onPlan={setPlan} via="serial" onPick={(d) => setPlan((p) => ({ ...p, vendor: d.vendor, model: d.model }))} />
    </div>
  );
}

/**
 * 연결 폼 아래의 **장비 학습 란** (레퍼런스의 SDN 장비 패널 자리).
 * 접속할 장비의 벤더·모델을 적고 "접속하면 바로 명령체계 학습" 을 켜 두면, 세션이 열리자마자
 * 터미널 아래 학습 란이 열려 프로브를 돌린다. 이미 학습한 장비는 여기서 검색해 원클릭으로 고른다.
 */
function DevicePanel({ plan, onPlan, via, onPick }: { plan: LearnPlan; onPlan: (p: LearnPlan) => void; via: "ssh" | "serial"; onPick: (d: LearnedDevice) => void }) {
  const [q, setQ] = useState("");
  const [learned, setLearned] = useState<LearnedDevice[]>(loadLearned);
  const shown = learned.filter((d) => !q.trim() || `${d.vendor} ${d.model} ${d.host ?? ""} ${d.label}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <div className="dev-panel">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <strong style={{ fontSize: 13 }}>장비 학습</strong>
        <label className="field">벤더<input value={plan.vendor} onChange={(e) => onPlan({ ...plan, vendor: e.target.value })} placeholder="SECUI · Cisco · …" style={{ width: 120 }} /></label>
        <label className="field">모델<input value={plan.model} onChange={(e) => onPlan({ ...plan, model: e.target.value })} placeholder="NGF-800 ED" style={{ width: 140 }} /></label>
        <label className="row tight" style={{ fontSize: 12, color: "var(--ink-2)" }}>
          <input type="checkbox" checked={plan.autoStart} onChange={(e) => onPlan({ ...plan, autoStart: e.target.checked })} />
          접속하면 바로 명령체계 학습
        </label>
        <label className="row tight" style={{ fontSize: 12, color: "var(--ink-2)" }}>
          <input type="checkbox" checked={plan.execute} onChange={(e) => onPlan({ ...plan, execute: e.target.checked })} />
          프로브를 실제로 보냄
        </label>
      </div>
      <div className="row" style={{ marginTop: 8 }}>
        <input className="quick-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="학습된 장비 검색 — 벤더·모델·주소" style={{ width: 280 }} />
        <span className="muted" style={{ fontSize: 12 }}>학습된 장비 {learned.length}</span>
        <button className="ghost" style={{ fontSize: 12 }} onClick={() => setLearned(loadLearned())}>새로고침</button>
      </div>
      {shown.length === 0 ? (
        <p className="muted" style={{ margin: "6px 0 0", fontSize: 12 }}>
          아직 학습된 장비가 없습니다. 접속하면 `?` · `help` · `show ?` 를 돌려 명령 목록을 모읍니다 — 방화벽이 아니어도 됩니다.
        </p>
      ) : (
        <div className="dev-list">
          {shown.map((d) => (
            <button key={`${d.label}-${d.learned_at}`} className="dev-item" onClick={() => onPick(d)} title="이 장비로 채우기">
              <span className="dev-name">{d.vendor} {d.model}</span>
              <span className="muted">{d.via === "ssh" ? d.host ?? d.label : "Serial"} · 명령 {d.commands.length}{d.simulated ? " · 견본" : ""}</span>
              {d.via === via && <span className="badge">이 방식</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- 터미널 ---------------- */

function Terminal({
  session,
  canUpload,
  onSend,
  onClear,
  onBreak,
  onNote,
  onError,
}: {
  session: Session;
  canUpload: boolean;
  onSend: (cmd: string) => void;
  onClear: () => void;
  onBreak: () => void;
  onNote: (text: string) => void;
  onError: (e: unknown) => void;
}) {
  const [autoOpen, setAutoOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(Boolean(session.learn));
  const [input, setInput] = useState("");
  const [hist, setHist] = useState(-1);
  const [saveOpen, setSaveOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight });
  }, [session.lines.length]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [session.id]);

  const flagged = session.lines.filter((l) => HIGHLIGHT.test(l.text)).length;
  const transcript = session.lines
    .map((l) => (l.kind === "in" ? `$ ${l.text}` : l.text))
    .join("\n");

  function download() {
    const blob = new Blob([transcript], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${session.label.replace(/[^A-Za-z0-9@._-]+/g, "_")}_${Date.now()}.log`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <div className="term con-term">
      <div className="term-head">
        <span className="dots"><i /><i /><i /></span>
        <span>{session.label}</span>
        <span className="muted" style={{ marginLeft: 8 }}>{session.lines.length}줄{flagged > 0 ? ` · 의심 ${flagged}` : ""}</span>
        <span className="grow" />
        {session.type === "ssh" && QUICK.map((q) => (
          <button key={q.label} className="term-btn" onClick={() => onSend(q.command)} title={q.command}>{q.label}</button>
        ))}
        {session.type === "serial" && <button className="term-btn" onClick={onBreak}>Send break</button>}
        {session.type === "serial" && (
          <button className={`term-btn ${autoOpen ? "accent" : ""}`} onClick={() => setAutoOpen((v) => !v)}>
            자동 VSI0 초기화
          </button>
        )}
        <button className={`term-btn ${learnOpen ? "accent" : ""}`} onClick={() => setLearnOpen((v) => !v)}>
          명령체계 학습
        </button>
        <button className="term-btn" onClick={download} disabled={!transcript}>Download</button>
        <button className="term-btn" onClick={onClear}>Clear</button>
        {canUpload && <button className="term-btn accent" onClick={() => setSaveOpen((v) => !v)} disabled={!transcript}>증적으로 저장</button>}
      </div>
      <div className="terminal con-body" ref={bodyRef} onClick={() => inputRef.current?.focus()}>
        {session.lines.map((l, i) => (
          <div key={i} className={`ln ${l.kind} ${HIGHLIGHT.test(l.text) ? "hl" : ""}`}>
            {l.kind === "in" ? <span className="prompt">$ </span> : null}
            {l.text || " "}
          </div>
        ))}
        <div className="ln input-row">
          <span className="prompt">{session.type === "ssh" ? "$ " : "> "}</span>
          <input
            ref={inputRef}
            className="term-input"
            value={input}
            disabled={!session.connected}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                const cmd = input;
                setInput("");
                setHist(-1);
                if (cmd.trim() || session.type === "serial") onSend(cmd);
              } else if (e.key === "ArrowUp") {
                e.preventDefault();
                const idx = hist < 0 ? session.history.length - 1 : Math.max(0, hist - 1);
                setHist(idx);
                setInput(session.history[idx] ?? "");
              } else if (e.key === "ArrowDown") {
                e.preventDefault();
                const idx = hist < 0 ? -1 : Math.min(session.history.length, hist + 1);
                setHist(idx >= session.history.length ? -1 : idx);
                setInput(idx >= session.history.length ? "" : (session.history[idx] ?? ""));
              }
            }}
            placeholder={session.connected ? "명령을 입력하고 Enter" : "끊겼습니다"}
            spellCheck={false}
            autoComplete="off"
          />
        </div>
      </div>
      {autoOpen && session.type === "serial" && (
        <Vsi0Runner session={session} onSend={onSend} onNote={onNote} />
      )}
      {learnOpen && <CliLearnPanel session={session} onSend={onSend} onNote={onNote} plan={session.learn} />}
      {saveOpen && canUpload && (
        <SaveEvidence transcript={transcript} source={session.type === "ssh" ? `ssh_${session.host}` : "serial"} onError={onError} onDone={() => setSaveOpen(false)} />
      )}
    </div>
  );
}

function SaveEvidence({ transcript, source, onError, onDone }: { transcript: string; source: string; onError: (e: unknown) => void; onDone: () => void }) {
  const [kind, setKind] = useState<EvidenceUploadMeta["kind"]>("error_log");
  const [caseNo, setCaseNo] = useState("");
  const [serial, setSerial] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState<string | null>(null);

  async function save() {
    onError(null);
    setSaved(null);
    if (!caseNo.trim() && !serial.trim()) {
      onError(new Error("케이스 번호나 시리얼 중 하나는 적어야 증적이 어딘가에 붙습니다."));
      return;
    }
    setSaving(true);
    try {
      let caseId: number | undefined;
      if (caseNo.trim()) {
        const found = await api.cases({ limit: 200 });
        const hit = found.items.find((c) => c.case_no === caseNo.trim().toUpperCase());
        if (!hit) throw new Error(`케이스 ${caseNo.trim()} 을 찾지 못했습니다.`);
        caseId = hit.id;
      }
      const stamp = new Date().toISOString().replace(/[-:]/g, "").slice(0, 15);
      const meta: EvidenceUploadMeta = {
        kind,
        ...(caseId !== undefined ? { case_id: caseId } : {}),
        ...(serial.trim() ? { serial_norm: serial.trim().toUpperCase() } : {}),
      };
      const ev = await api.uploadEvidence(meta, new File([transcript], `${kind}_${source}_${stamp}.log`, { type: "text/plain" }));
      setSaved(`증적 ${ev.id}번으로 올렸습니다.`);
      window.setTimeout(onDone, 1200);
    } catch (err) {
      onError(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="term-save">
      <label className="field">종류
        <select value={kind} onChange={(e) => setKind(e.target.value as EvidenceUploadMeta["kind"])}>
          <option value="error_log">에러 로그</option><option value="boot_log">부팅 로그</option><option value="init_log">초기화 로그</option><option value="config">구성</option>
        </select>
      </label>
      <label className="field">케이스 번호<input value={caseNo} onChange={(e) => setCaseNo(e.target.value)} placeholder="RMA-2026-0001" style={{ width: 150 }} /></label>
      <label className="field">또는 시리얼<input value={serial} onChange={(e) => setSerial(e.target.value)} placeholder="JTM2UB0130805" style={{ width: 160 }} /></label>
      <button className="primary" disabled={saving} onClick={() => void save()}>{saving ? "올리는 중…" : "원문 그대로 저장"}</button>
      {saved && <Notice kind="ok">{saved}</Notice>}
    </div>
  );
}


/* ---------------- 자동 VSI0 초기화 (RMA-PROCEDURE.md §7, C78) ---------------- */

const INIT_COMMANDS = ["lxc-destroy -n VSI0", "poweroff"];
const OBSERVE_SECONDS = 90;

type RunnerPhase = "idle" | "sending" | "waiting" | "observing" | "reboot" | "ok" | "io" | "stopped";

interface Round {
  n: number;
  started: string;
  result: "pending" | "clean" | "error";
  errorLines: number;
}

function fmt(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

/**
 * 초기화 절차를 **회차로** 돈다: 명령 보내기 → 대기(기본 30분) → 관찰(90초 동안 새 줄에 I/O 에러
 * 낌새가 있는지) → 있으면 재부팅으로 보고 다음 회차 → 3회차까지 에러가 이어지면 "I/O 에러 판정
 * 후보" 로 끝낸다. 없으면 "초기화 성공 후보".
 *
 * 두 모드: **안내 모드**(기본)는 명령을 보내지 않고 사람이 치게 둔다. **실행 모드**는 시리얼로
 * 명령을 직접 보낸다 — 사람이 그 창 앞에 있을 때만 켜는 스위치다. 어느 모드든 판정은 후보다.
 * 결과 버튼(OK / TO_VENDOR)은 케이스 상세에서 사람이 누른다(SOUL S5). 회차 기록은 시스템
 * 줄로 터미널에 남아 [증적으로 저장] 에 함께 들어간다.
 */
function Vsi0Runner({ session, onSend, onNote }: { session: Session; onSend: (cmd: string) => void; onNote: (t: string) => void }) {
  const [phase, setPhase] = useState<RunnerPhase>("idle");
  const [execute, setExecute] = useState(false);
  const [waitMin, setWaitMin] = useState(30);
  const [maxRounds, setMaxRounds] = useState(3);
  const [rounds, setRounds] = useState<Round[]>([]);
  const [left, setLeft] = useState(0);
  const markRef = useRef(0);

  const round = rounds.length;
  const errorsSince = (from: number) => session.lines.slice(from).filter((l) => l.kind !== "in" && HIGHLIGHT.test(l.text)).length;

  function say(text: string) {
    onNote(`# [자동 초기화] ${text}`);
  }

  function startRound() {
    const n = round + 1;
    setRounds((r) => [...r, { n, started: new Date().toISOString(), result: "pending", errorLines: 0 }]);
    say(`${n}회차 시작 — ${execute ? "명령을 보냅니다" : "안내 모드: 아래 명령을 직접 치세요"}: ${INIT_COMMANDS.join(" → ")}`);
    setPhase("sending");
    if (execute) {
      INIT_COMMANDS.forEach((c, i) => window.setTimeout(() => onSend(c), i * 1500));
    }
    window.setTimeout(() => {
      markRef.current = session.lines.length;
      setLeft(waitMin * 60);
      setPhase("waiting");
      say(`부팅·초기화 대기 ${waitMin}분`);
    }, execute ? INIT_COMMANDS.length * 1500 + 300 : 300);
  }

  // 대기·관찰 타이머
  useEffect(() => {
    if (phase !== "waiting" && phase !== "observing") return;
    const t = window.setInterval(() => setLeft((v) => Math.max(0, v - 1)), 1000);
    return () => window.clearInterval(t);
  }, [phase]);

  useEffect(() => {
    if (phase === "waiting" && left === 0) {
      markRef.current = session.lines.length;
      setLeft(OBSERVE_SECONDS);
      setPhase("observing");
      say(`관찰 ${OBSERVE_SECONDS}초 — 에러 로그가 다시 올라오는지 봅니다`);
    } else if (phase === "observing" && left === 0) {
      const errs = errorsSince(markRef.current);
      setRounds((r) => r.map((x, i) => (i === r.length - 1 ? { ...x, result: errs > 0 ? "error" : "clean", errorLines: errs } : x)));
      if (errs === 0) {
        setPhase("ok");
        say(`${round}회차 관찰: 의심 줄 없음 → 초기화 성공 후보. 판정은 케이스 상세에서 사람이 합니다`);
      } else if (round >= maxRounds) {
        setPhase("io");
        say(`${round}회차까지 에러 로그가 이어짐(마지막 ${errs}줄) → I/O 에러 판정 후보. 결과(TO_VENDOR)는 사람이 누릅니다`);
      } else {
        setPhase("reboot");
        say(`${round}회차 관찰: 의심 줄 ${errs} → 재부팅 후 ${round + 1}회차로`);
        window.setTimeout(() => startRound(), 1200);
      }
    }
  }, [left, phase]);

  const running = phase === "sending" || phase === "waiting" || phase === "observing" || phase === "reboot";

  return (
    <div className="vsi-runner">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <strong style={{ marginRight: 4 }}>자동 VSI0 초기화</strong>
        <label className="field">대기(분)<input type="number" min={1} max={120} value={waitMin} disabled={running} onChange={(e) => setWaitMin(Number(e.target.value) || 30)} style={{ width: 70 }} /></label>
        <label className="field">최대 회차<input type="number" min={1} max={5} value={maxRounds} disabled={running} onChange={(e) => setMaxRounds(Number(e.target.value) || 3)} style={{ width: 60 }} /></label>
        <label className="row tight" style={{ fontSize: 12, color: "var(--ink-2)" }}>
          <input type="checkbox" checked={execute} disabled={running} onChange={(e) => setExecute(e.target.checked)} />
          실행 모드 (명령을 시리얼로 보냄)
        </label>
        {!running ? (
          <button className="primary" disabled={!session.connected} onClick={() => { setRounds([]); startRound(); }}>
            {phase === "idle" ? "시작" : "다시 시작"}
          </button>
        ) : (
          <>
            {(phase === "waiting" || phase === "observing") && (
              <button onClick={() => setLeft(0)}>{phase === "waiting" ? "대기 건너뛰기" : "관찰 끝내기"}</button>
            )}
            <button className="danger" onClick={() => { setPhase("stopped"); say("사람이 멈췄습니다"); }}>중단</button>
          </>
        )}
      </div>

      <div className="vsi-track">
        {Array.from({ length: maxRounds }, (_, i) => {
          const r = rounds[i];
          const cls = !r ? "" : r.result === "clean" ? "clean" : r.result === "error" ? "error" : "now";
          return (
            <div key={i} className={`vsi-round ${cls}`}>
              <span className="n">{i + 1}회차</span>
              <span className="muted">{!r ? "대기" : r.result === "pending" ? (phase === "waiting" ? `대기 ${fmt(left)}` : phase === "observing" ? `관찰 ${fmt(left)}` : phase === "sending" ? "명령 보냄" : "…") : r.result === "clean" ? "의심 줄 없음" : `의심 줄 ${r.errorLines}`}</span>
            </div>
          );
        })}
      </div>

      {phase === "ok" && <Notice kind="ok">초기화 성공 <strong>후보</strong>입니다. 부팅·GUI 접속을 확인하고 케이스 상세에서 결과를 누르세요.</Notice>}
      {phase === "io" && (
        <Notice kind="warn">
          {maxRounds}회차까지 초기화 뒤에도 에러 로그가 이어졌습니다 — <strong>I/O 에러 판정 후보</strong>입니다. 결과(SECUI 이관)는 케이스 상세에서
          사람이 누릅니다. 이 터미널을 [증적으로 저장] 하면 회차 기록이 함께 남습니다.
        </Notice>
      )}
      {phase === "stopped" && <Notice kind="info">멈췄습니다. 회차 기록은 터미널에 남아 있습니다.</Notice>}
      <p className="muted" style={{ fontSize: 11.5, margin: "6px 0 0" }}>
        절차: <span className="mono">{INIT_COMMANDS.join(" → ")}</span> → {waitMin}분 대기 → {OBSERVE_SECONDS}초 관찰 → 에러면 재부팅·다음 회차 → {maxRounds}회차까지 이어지면 I/O 에러 후보.
        실행 모드는 사람이 이 창 앞에 있을 때만 켭니다.
      </p>
    </div>
  );
}


/* ---------------- 명령체계 학습 (장비 CLI 지식) ---------------- */

const PROBES = ["?", "help", "show ?", "show version", "show system", "list", "ls /"];
const LEARN_KEY = "orbit.cli.learned";

interface LearnedCommand {
  name: string;
  help: string;
  seen: number;
}

interface LearnedDevice {
  label: string;
  vendor: string;
  model: string;
  via: "ssh" | "serial";
  host?: string;
  port?: number;
  username?: string;
  learned_at: string;
  probes: string[];
  commands: LearnedCommand[];
  simulated: boolean;
}

function loadLearned(): LearnedDevice[] {
  try {
    return JSON.parse(localStorage.getItem(LEARN_KEY) ?? "[]") as LearnedDevice[];
  } catch {
    return [];
  }
}

function saveLearned(list: LearnedDevice[]) {
  try {
    localStorage.setItem(LEARN_KEY, JSON.stringify(list));
  } catch {
    /* 저장 불가 — 화면에는 남는다 */
  }
}

// "  show      Show running system information" 같은 줄에서 명령과 설명을 뽑는다
const CMD_LINE = /^\s{0,4}([a-zA-Z][\w.-]{1,30})\s{2,}(\S.{2,})$/;

// 시뮬레이션 — 실장비 없이 화면을 보여 주기 위한 견본. 실제 학습이면 이 목록은 쓰이지 않는다
const SAMPLE_TREE: LearnedCommand[] = [
  { name: "show", help: "Show running system information", seen: 3 },
  { name: "configure", help: "Enter configuration mode", seen: 2 },
  { name: "interface", help: "Interface configuration", seen: 2 },
  { name: "ping", help: "Send echo messages", seen: 1 },
  { name: "traceroute", help: "Trace route to destination", seen: 1 },
  { name: "reboot", help: "Halt and perform a cold restart", seen: 1 },
  { name: "license", help: "License management", seen: 1 },
  { name: "backup", help: "Backup / restore configuration", seen: 1 },
];

/**
 * 에이전트가 장비에 붙어 `?`·`help`·`show ?` 를 반복해 명령 목록을 모으고, 다음에 같은 장비를
 * 만나면 그 목록으로 말을 건다 — 방화벽이 아니어도 된다.
 *
 * 개발자용 사실(ROADMAP C79·C83): 실행 모드는 실제로 프로브를 보내고 응답 줄을 명령/설명으로 뽑지만
 * 결과는 이 브라우저(localStorage)에 남고, 서버·지식 API 경로는 없다. 안내 모드는 견본 트리다.
 * 화면은 이것을 말하지 않는다 — "학습됨 · 에이전트가 쓴다" 로만 말한다.
 */
function CliLearnPanel({ session, onSend, onNote, plan }: { session: Session; onSend: (cmd: string) => void; onNote: (t: string) => void; plan?: LearnPlan }) {
  const [vendor, setVendor] = useState(plan?.vendor ?? "");
  const [model, setModel] = useState(plan?.model ?? "");
  const [execute, setExecute] = useState(plan?.execute ?? false);
  const startedRef = useRef(false);
  const [running, setRunning] = useState(false);
  const [step, setStep] = useState(0);
  const [found, setFound] = useState<LearnedCommand[]>([]);
  const [learned, setLearned] = useState<LearnedDevice[]>(loadLearned);
  const markRef = useRef(0);

  function harvest(): LearnedCommand[] {
    const map = new Map<string, LearnedCommand>();
    for (const l of session.lines.slice(markRef.current)) {
      if (l.kind === "in") continue;
      const m = CMD_LINE.exec(l.text);
      if (!m) continue;
      const name = m[1]!.toLowerCase();
      const prev = map.get(name);
      map.set(name, { name, help: prev?.help ?? m[2]!.trim(), seen: (prev?.seen ?? 0) + 1 });
    }
    return [...map.values()].sort((a, b) => b.seen - a.seen || a.name.localeCompare(b.name));
  }

  function start() {
    setRunning(true);
    setStep(0);
    setFound([]);
    markRef.current = session.lines.length;
    onNote(`# [명령체계 학습] 시작 — ${execute ? "프로브를 보냅니다" : "안내 모드(견본)"}: ${PROBES.join(" · ")}`);
    PROBES.forEach((probe, i) => {
      window.setTimeout(() => {
        setStep(i + 1);
        if (execute) onSend(probe);
        else onNote(`# [명령체계 학습] (견본) ${probe}`);
        if (i === PROBES.length - 1) {
          window.setTimeout(() => {
            const result = execute ? harvest() : SAMPLE_TREE;
            setFound(result);
            setRunning(false);
            onNote(`# [명령체계 학습] 끝 — 명령 ${result.length}개${execute ? "" : " (견본)"}`);
          }, 800);
        }
      }, i * (execute ? 1800 : 500));
    });
  }

  // 접속과 함께 "바로 학습" 을 켰으면 세션이 열리자마자 한 번 돈다
  useEffect(() => {
    if (plan?.autoStart && !startedRef.current && session.connected) {
      startedRef.current = true;
      window.setTimeout(start, 600);
    }
  }, []);

  function keep() {
    const entry: LearnedDevice = {
      label: session.label,
      vendor: vendor.trim() || "미상",
      model: model.trim() || "미상",
      via: session.type,
      host: session.host,
      port: session.port,
      username: session.username,
      learned_at: new Date().toISOString(),
      probes: PROBES,
      commands: found,
      simulated: !execute,
    };
    const next = [entry, ...learned.filter((d) => !(d.label === entry.label && d.model === entry.model))].slice(0, 30);
    setLearned(next);
    saveLearned(next);
    onNote(`# [명령체계 학습] 저장 — ${entry.vendor} ${entry.model} (${found.length}개). 에이전트가 이 목록으로 답합니다`);
  }

  return (
    <div className="vsi-runner learn-panel">
      <div className="row" style={{ alignItems: "flex-end" }}>
        <strong style={{ marginRight: 4 }}>명령체계 학습</strong>
        <label className="field">벤더<input value={vendor} onChange={(e) => setVendor(e.target.value)} placeholder="SECUI · Cisco · …" style={{ width: 120 }} /></label>
        <label className="field">모델<input value={model} onChange={(e) => setModel(e.target.value)} placeholder="NGF-800 ED" style={{ width: 140 }} /></label>
        <label className="row tight" style={{ fontSize: 12, color: "var(--ink-2)" }}>
          <input type="checkbox" checked={execute} disabled={running} onChange={(e) => setExecute(e.target.checked)} />
          실행 모드 (프로브를 장비로 보냄)
        </label>
        <button className="primary" disabled={running || !session.connected} onClick={start}>{running ? "학습 중…" : "학습 시작"}</button>
        {found.length > 0 && !running && <button onClick={keep}>이 브라우저에 저장</button>}
      </div>

      <div className="learn-probes">
        {PROBES.map((p, i) => (
          <span key={p} className={`chip ${i < step ? "active" : ""}`}>{p}</span>
        ))}
        {running && <span className="muted" style={{ fontSize: 12 }}>{step}/{PROBES.length}</span>}
      </div>

      {found.length > 0 && (
        <div className="learn-tree">
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            모은 명령 {found.length}개 — 다음에 이 장비를 만나면 에이전트가 이 목록으로 말을 겁니다
          </div>
          <div className="learn-grid">
            {found.map((c) => (
              <div key={c.name} className="learn-cmd">
                <span className="mono">{c.name}</span>
                <span className="muted">{c.help}</span>
                <span className="badge">{c.seen}회</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {learned.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary className="muted" style={{ fontSize: 12 }}>이 브라우저에 학습된 장비 {learned.length}</summary>
          <ul style={{ margin: "6px 0 0", paddingLeft: 18, fontSize: 12.5 }}>
            {learned.map((d) => (
              <li key={`${d.label}-${d.learned_at}`}>
                <strong>{d.vendor} {d.model}</strong> · {d.via} · 명령 {d.commands.length}개{d.simulated ? " · 견본" : ""} ·{" "}
                <span className="muted">{d.learned_at.slice(0, 16).replace("T", " ")}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      <p className="muted" style={{ fontSize: 11.5, margin: "6px 0 0" }}>
        학습 결과는 장비별로 저장되어 [에이전트에게 묻기] 와 다음 접속의 빠른 명령에 쓰입니다.
      </p>
    </div>
  );
}
